import { asyncRequest } from './asyncRequest'

// Constants for all CourseGroupControllerV2 endpoints
// Base route: api/course/group/v2
export const studentCourseConstants = {
    // POST {courseGroupId}/l1 — Set the L1 language for a course group
    STUDENT_COURSE_GROUP_SET_L1: new asyncRequest('STUDENT_COURSE_GROUP_SET_L1'),

    // GET (root) — Get all course groups for user with overview metadata
    STUDENT_COURSE_GROUP_GET_OVERVIEW: new asyncRequest('STUDENT_COURSE_GROUP_GET_OVERVIEW'),

    // GET {courseGroupId}/l1/{l1Id} — Get full course group content for a specific L1
    STUDENT_COURSE_GROUP_GET_BY_ID: new asyncRequest('STUDENT_COURSE_GROUP_GET_BY_ID'),

    // GET {courseGroupId}/l1/{l1Id}/progression — Get course group progression for user
    STUDENT_COURSE_GROUP_GET_PROGRESSION: new asyncRequest('STUDENT_COURSE_GROUP_GET_PROGRESSION'),

    // GET {courseGroupId}/l1/{l1Id}/course/{topicId}/lesson/order — Get lesson order for a topic
    STUDENT_COURSE_GET_LESSON_ORDER: new asyncRequest('STUDENT_COURSE_GET_LESSON_ORDER'),

    // GET {courseGroupId}/l1/{l1Id}/course/{topicId}/lesson/order/progression — Get lesson order progression
    STUDENT_COURSE_GET_LESSON_ORDER_PROGRESSION: new asyncRequest('STUDENT_COURSE_GET_LESSON_ORDER_PROGRESSION'),

    // GET {courseGroupId}/l1/{l1Id}/course/{topicId}/lesson/{customLessonId}/scenario/v1/ — Get scenario lesson
    STUDENT_COURSE_GET_LESSON: new asyncRequest('STUDENT_COURSE_GET_LESSON'),

    // POST {courseGroupId}/l1/{l1Id}/course/{topicId}/lesson/{customLessonId}/scenario/v3/ — Report lesson answers
    STUDENT_COURSE_REPORT_LESSON: new asyncRequest('STUDENT_COURSE_REPORT_LESSON'),

    // GET image/file/{fileReferenceId} — Fetch an image via the student image endpoint (returns blob)
    STUDENT_COURSE_FETCH_IMAGE: new asyncRequest('STUDENT_COURSE_FETCH_IMAGE'),

    // GET sound/file/{fileReferenceId} — Fetch audio via the student sound endpoint (returns blob)
    STUDENT_COURSE_FETCH_AUDIO: new asyncRequest('STUDENT_COURSE_FETCH_AUDIO'),

    // Synchronous UI state — remember the selected course group and topic in the main menu
    STUDENT_COURSE_SET_SELECTED_COURSE_GROUP_ID: 'STUDENT_COURSE_SET_SELECTED_COURSE_GROUP_ID',
    STUDENT_COURSE_SET_SELECTED_TOPIC_ID: 'STUDENT_COURSE_SET_SELECTED_TOPIC_ID',
}

export const studentCourseActions = {
    // Set L1 language for a course group
    setCourseGroupL1: (courseGroupId: string, l1Id: number) => {
        return {
            type: studentCourseConstants.STUDENT_COURSE_GROUP_SET_L1.REQUEST,
            payload: {
                courseGroupId,
                l1Id,
            },
        }
    },

    // Get all course groups overview for the logged-in user
    getCourseGroupOverview: (deviceId?: string) => {
        return {
            type: studentCourseConstants.STUDENT_COURSE_GROUP_GET_OVERVIEW.REQUEST,
            payload: {
                deviceId,
            },
        }
    },

    // Get a specific course group by ID for a given L1 language
    getCourseGroupById: (courseGroupId: string, l1Id: number) => {
        return {
            type: studentCourseConstants.STUDENT_COURSE_GROUP_GET_BY_ID.REQUEST,
            payload: {
                courseGroupId,
                l1Id,
            },
        }
    },

    // Get course group progression for a given L1 language
    getCourseGroupProgression: (courseGroupId: string, l1Id: number) => {
        return {
            type: studentCourseConstants.STUDENT_COURSE_GROUP_GET_PROGRESSION.REQUEST,
            payload: {
                courseGroupId,
                l1Id,
            },
        }
    },

    // Get the lesson order for a specific topic within a course group
    getCourseLessonOrder: (courseGroupId: string, l1Id: number, topicId: number) => {
        return {
            type: studentCourseConstants.STUDENT_COURSE_GET_LESSON_ORDER.REQUEST,
            payload: {
                courseGroupId,
                l1Id,
                topicId,
            },
        }
    },

    // Get the lesson order progression for a specific topic
    getCourseLessonOrderProgression: (courseGroupId: string, l1Id: number, topicId: number) => {
        return {
            type: studentCourseConstants.STUDENT_COURSE_GET_LESSON_ORDER_PROGRESSION.REQUEST,
            payload: {
                courseGroupId,
                l1Id,
                topicId,
            },
        }
    },

    // Get a specific lesson (scenario v1) for playing
    getLesson: (courseGroupId: string, l1Id: number, topicId: number, customLessonId: string, generationIndex?: number) => {
        return {
            type: studentCourseConstants.STUDENT_COURSE_GET_LESSON.REQUEST,
            payload: {
                courseGroupId,
                l1Id,
                topicId,
                customLessonId,
                generationIndex,
            },
        }
    },

    // Report lesson results (scenario v3 report)
    reportLesson: (courseGroupId: string, l1Id: number, topicId: number, customLessonId: string, scenarioReport: any) => {
        return {
            type: studentCourseConstants.STUDENT_COURSE_REPORT_LESSON.REQUEST,
            payload: {
                courseGroupId,
                l1Id,
                topicId,
                customLessonId,
                scenarioReport,
            },
        }
    },

    // Fetch an image from the student image endpoint (imageUrl format: "image/file/{fileReferenceId}")
    fetchImage: (imageUrl: string) => {
        return {
            type: studentCourseConstants.STUDENT_COURSE_FETCH_IMAGE.REQUEST,
            payload: {
                imageUrl,
            },
        }
    },

    // Fetch audio from the student sound endpoint (audioUrl format: "sound/file/{fileReferenceId}")
    fetchAudio: (audioUrl: string) => {
        return {
            type: studentCourseConstants.STUDENT_COURSE_FETCH_AUDIO.REQUEST,
            payload: {
                audioUrl,
            },
        }
    },

    // Set the selected course group ID in the main menu (persisted across navigation)
    setSelectedCourseGroupId: (courseGroupId: string | null) => {
        return {
            type: studentCourseConstants.STUDENT_COURSE_SET_SELECTED_COURSE_GROUP_ID,
            payload: {
                courseGroupId,
            },
        }
    },

    // Set the selected topic ID in the course detail panel (persisted across navigation)
    setSelectedTopicId: (topicId: number | null) => {
        return {
            type: studentCourseConstants.STUDENT_COURSE_SET_SELECTED_TOPIC_ID,
            payload: {
                topicId,
            },
        }
    },
}
